import Helper from '@ember/component/helper';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import dayjs from 'dayjs';
import type {
  FrequencyType,
  ReportType,
} from 'frontend-utils/utils/constants/constant-values';
import {
  FREQUENCY_TYPE_DAILY,
  ISO_DAYS,
  REPORT_TYPE_PARITY_INSIGHT,
} from 'frontend-utils/utils/constants/constant-values';

type DayOfWeek = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface DisplayFrequencySignature {
  Args: {
    Positional: [DayOfWeek[], number, ReportType, FrequencyType];
  };
  Return: string;
}

export default class DisplayFrequencyHelper extends Helper<DisplayFrequencySignature> {
  @service declare intl: Services['intl'];

  compute([
    days,
    frequency,
    reportType,
    shopconfigFrequency,
  ]: DisplayFrequencySignature['Args']['Positional']): DisplayFrequencySignature['Return'] {
    let res = '';
    // limited parity reports have a custom string because we don't know the day they are sent to the user
    if (reportType === REPORT_TYPE_PARITY_INSIGHT) {
      return shopconfigFrequency === FREQUENCY_TYPE_DAILY
        ? this.intl.t('reports.frequency_daily')
        : this.intl.t('reports.frequency_weekly');
    }

    if (isEmpty(days) || isEmpty(frequency)) {
      return res;
    }

    if (frequency === 1 || frequency === 0) {
      res += this.intl.t('reports.frequency_weekly');
    } else {
      res += this.intl.t('reports.frequency_non_weekly', { count: frequency });
    }

    if (days.length && days.length !== 7) {
      if (days.length === 2 && days.includes('sat') && days.includes('sun')) {
        res = this.intl.t('reports.frequency_weekends');
      } else if (
        days.length === 5 &&
        days.includes('mon') &&
        days.includes('tue') &&
        days.includes('wed') &&
        days.includes('thu') &&
        days.includes('fri')
      ) {
        res = this.intl.t('reports.frequency_weekdays');
      } else {
        const dayStrings: string[] = [];
        let dayStringResult = '';

        const sortOrder = {
          sun: 0,
          mon: 1,
          tue: 2,
          wed: 3,
          thu: 4,
          fri: 5,
          sat: 6,
        };

        if (dayjs.localeData().firstDayOfWeek() === ISO_DAYS.mon) {
          sortOrder.sun = 7;
        }

        days.sort((a, b) => (sortOrder[a] > sortOrder[b] ? 1 : -1));

        days.forEach((day) => {
          dayStrings.push(this.intl.t(`days_short.${day}`));
        });
        dayStringResult += dayStrings.join(', ');

        res += ` ${this.intl.t('general.on_date', { days: dayStringResult })} `;
      }
    } else {
      res = this.intl.t('reports.frequency_daily');
    }

    return res;
  }
}
